import React, { useMemo, useState } from 'react'
import { Modal, ModalBody, ModalHeader, ModalFooter } from '@/components/ui-compat'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Query, Mutation, Enums } from '../../../api'
import { useUser } from '../../../hooks'
import Grid from '../../../components/grid'
import Loading from '../../../components/Loading'
import { UserPlus, Loader2, FileDown, UserCheck, UserX } from 'lucide-react'
import { cn } from '@/lib/utils'
import FileUpload from '../../../components/form/FileUpload'
import Pappa from 'papaparse'

import { Form, Formik } from 'formik'
import * as y from 'yup'
import Input from '../../../components/form/FormikInput'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import i18next from 'i18next'

const UserModal = ({ editUserValues, isOpen, onClose, onSaved }) => {
  const { t } = useTranslation()
  const me = useUser()
  const { data: projectsData } = useQuery(Query.PROJECTS)
  const { data: rolesData } = useQuery(Query.PROJECT_ROLES)
  const [createUser, createUserData] = useMutation(Mutation.CREATE_USER)
  const [updateUser] = useMutation(Mutation.UPDATE_USER)
  const [updateUserRights] = useMutation(Mutation.UPDATE_USER_RIGHTS)
  const [addProjectUsers] = useMutation(Mutation.ADD_PROJECT_USERS)
  const [removeProjectUsers] = useMutation(Mutation.REMOVE_PROJECT_USERS)
  const [setUserProjectRole] = useMutation(Mutation.SET_USER_PROJECT_ROLE)

  const handleClose = () => {
    onClose()
  }

  const projects = projectsData?.projects || []
  const roles = rolesData?.projectRoles || []
  const roleLabel = (role) => (role.key ? t('role_' + role.key, role.name) : role.name)
  const defaultRoleId = roles.find((r) => r.key === 'SUBCONTRACTOR')?.id ?? roles[0]?.id ?? ''

  const isSelf = !!editUserValues?.id && editUserValues.id === (me as any)?.id
  const actorIsOwner = (me as any)?.rights === 'OWNER'

  // Current assignments: { projectId: roleId }, plus membership row ids for role changes
  const currentAssignments: Record<string, string> = {}
  const utpIdByProject: Record<string, string> = {}
  if (editUserValues?.userToProjects) {
    editUserValues.userToProjects.forEach((utp) => {
      currentAssignments[utp.project.id] = utp.roleId || ''
      utpIdByProject[utp.project.id] = utp.id
    })
  }

  const initialValues = editUserValues
    ? { ...editUserValues, projectAssignments: { ...currentAssignments } }
    : {
        name: '',
        email: '',
        company: '',
        rights: null,
        projectAssignments: {} as Record<string, string>,
      }

  return (
    <Modal isOpen={isOpen} toggle={() => handleClose()}>
      <ModalHeader toggle={() => handleClose()}>
        {editUserValues
          ? t('Organization_users_edit_user_modal_heading', 'Edit User')
          : t('Organization_users_new_user_modal_heading', 'New User')}
      </ModalHeader>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={y.object().shape({
          name: y
            .string()
            .min(3, t('Organization_users_new_user_modal_name_min'))
            .required(t('Organization_users_new_user_modal_name_required')),
          email: y
            .string()
            .email(t('Organization_users_new_user_modal_email_type_email'))
            .required(t('Organization_users_new_user_modal_email_required')),
          rights: y
            .string()
            .nullable()
            .required(t('Organization_users_new_user_modal_rights_required')),
        })}
        onSubmit={async (values) => {
          const { projectAssignments, userToProjects, ...userValues } = values

          try {
            let userId: string

            if (userValues.id) {
              await updateUser({
                variables: {
                  id: userValues.id,
                  name: userValues.name,
                  email: userValues.email,
                  company: userValues.company || null,
                },
              })
              userId = userValues.id
              if (!isSelf && userValues.rights !== editUserValues.rights) {
                await updateUserRights({ variables: { id: userValues.id, rights: userValues.rights } })
              }
            } else {
              const result = await createUser({
                variables: {
                  ...userValues,
                  company: userValues.company || null,
                  language: i18next.language,
                },
              })
              userId = result.data.createUser.id
            }

            const selectedProjectIds = Object.keys(projectAssignments)
            const currentProjectIds = Object.keys(currentAssignments)
            const toAdd = selectedProjectIds.filter((id) => !currentProjectIds.includes(id))
            const toRemove = currentProjectIds.filter((id) => !selectedProjectIds.includes(id))
            const toUpdate = selectedProjectIds.filter(
              (id) =>
                currentProjectIds.includes(id) &&
                !!projectAssignments[id] &&
                projectAssignments[id] !== currentAssignments[id]
            )

            for (const projectId of toAdd) {
              await addProjectUsers({
                variables: { id: projectId, users: [{ id: userId, roleId: projectAssignments[projectId] || defaultRoleId }] },
              })
            }

            for (const projectId of toRemove) {
              await removeProjectUsers({
                variables: { id: projectId, users: [userId] },
              })
            }

            for (const projectId of toUpdate) {
              await setUserProjectRole({
                variables: { id: utpIdByProject[projectId], roleId: projectAssignments[projectId] },
              })
            }

            await onSaved?.()
            handleClose()
          } catch (error) {
            toastr.error(t('Common_error', 'Error'), (error as Error).message)
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <ModalBody>
              <div className="space-y-4">
                <Input name="name" label={t('Organization_users_new_user_modal_name_label')} />
                <Input name="email" label={t('Organization_users_new_user_modal_email_label')} />
                <Input name="company" label={t('Members_col_company', 'Company')} />
                <Input
                  type="select"
                  name="rights"
                  disabled={isSelf || (!actorIsOwner && editUserValues?.rights === 'OWNER')}
                  label={t('Organization_users_new_user_modal_rights_label')}
                  placeholder={t('Organization_users_new_user_modal_rights_placeholder')}
                  options={Enums.RIGHTS.filter(
                    (r) => actorIsOwner || r !== 'OWNER' || editUserValues?.rights === 'OWNER'
                  ).map((r) => ({ label: t('rights_' + r), value: r }))}
                />

                {projects.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('Organization_users_new_user_modal_projects_label', 'Projects')}</label>
                    <div className="space-y-2">
                      {projects.map((project) => {
                        const isChecked = project.id in (values.projectAssignments || {})
                        const roleId = values.projectAssignments?.[project.id] || ''
                        return (
                          <div key={project.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`project-${project.id}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                const next = { ...(values.projectAssignments || {}) }
                                if (checked) {
                                  next[project.id] = defaultRoleId
                                } else {
                                  delete next[project.id]
                                }
                                setFieldValue('projectAssignments', next)
                              }}
                            />
                            <label
                              htmlFor={`project-${project.id}`}
                              className="text-sm leading-none cursor-pointer"
                            >
                              {project.name}
                            </label>
                            {isChecked && (
                              <Select
                                value={roleId}
                                onValueChange={(value) => {
                                  setFieldValue('projectAssignments', {
                                    ...(values.projectAssignments || {}),
                                    [project.id]: value,
                                  })
                                }}
                              >
                                <SelectTrigger className="h-8 w-auto ml-auto text-sm">
                                  <SelectValue placeholder={t('Organization_users_new_user_modal_role_placeholder', 'Select role…')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                      {roleLabel(r)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {createUserData.error && (
                  <p className="text-sm text-destructive">{createUserData.error.message}</p>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" type="button" disabled={isSubmitting} onClick={() => handleClose()}>
                {t('Common_cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('Organization_users_new_user_modal_save_btn')}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const UsersImportModal = ({ isOpen, users, onClose }) => {
  const { t, i18n } = useTranslation()
  const [createManyUsers, createManyUsersData] = useMutation(Mutation.CREATE_MANY_USERS, {
    onError: () => toastr.error(t('Procurement_bidders_error_loading_bidders_from_file'), ''),
  })

  const handleClose = () => {
    onClose()
  }

  const handleImport = async () => {
    await createManyUsers({
      variables: {
        users: users.map(({ name, email }) => ({ name, email })),
        language: i18n.language,
      },
      refetchQueries: [{ query: Query.USERS }],
    })

    handleClose()
  }

  const rows = users || []

  return (
    <Modal isOpen={isOpen} toggle={() => handleClose()}>
      <ModalHeader toggle={() => handleClose()}>
        {t('Organization_users_new_user_modal_new_user')}
      </ModalHeader>
      <ModalBody>
        <Grid
          getRowId={(u) => u.email}
          rows={rows}
          columns={[
            { name: 'name', title: t('Organization_users_new_user_modal_name') },
            { name: 'email', title: t('Organization_users_new_user_modal_email') },
          ]}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={() => handleClose()}>
          {t('Common_cancel', 'Cancel')}
        </Button>
        <Button onClick={handleImport}>
          {t('Organization_users_new_user_modal_import_all_btn')}
        </Button>
      </ModalFooter>
      {createManyUsersData.loading && <Loading />}
    </Modal>
  )
}

interface MemberRow {
  id: string
  name: string
  email: string
  deactivatedAt?: string | null
}

type MembershipMode = 'deactivate' | 'activate'

interface MembershipTarget {
  user: MemberRow
  mode: MembershipMode
}

/**
 * Confirmation for the reversible offboarding actions. Deactivation replaced
 * deletion here, so the destructive tone stays — it cuts sign-in and removes the
 * account from every work surface — while the copy makes the reversal explicit.
 */
const MembershipDialog: React.FC<{
  target: MembershipTarget | null
  saving: boolean
  onCancel: () => void
  onConfirm: () => void
}> = ({ target, saving, onCancel, onConfirm }) => {
  const { t } = useTranslation()
  // Radix keeps the content mounted through the close animation, so read the copy
  // from the last non-null target — otherwise it flips to the other mode as it fades.
  const lastTarget = React.useRef<MembershipTarget | null>(null)
  if (target) lastTarget.current = target
  const shown = target ?? lastTarget.current
  const isDeactivate = shown?.mode === 'deactivate'
  const name = shown?.user?.name || shown?.user?.email || ''

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && !saving && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isDeactivate
              ? t('Organization_users_deactivate_confirm_title')
              : t('Organization_users_activate_confirm_title')}
          </DialogTitle>
          <DialogDescription>
            {isDeactivate
              ? t('Organization_users_deactivate_confirm_body', { name })
              : t('Organization_users_activate_confirm_body', { name })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            {t('Common_cancel', 'Cancel')}
          </Button>
          <Button variant={isDeactivate ? 'destructive' : 'default'} onClick={onConfirm} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isDeactivate ? (
              <UserX className="h-4 w-4" />
            ) : (
              <UserCheck className="h-4 w-4" />
            )}
            {isDeactivate
              ? t('Organization_users_deactivate_btn')
              : t('Organization_users_activate_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type Audience = 'all' | 'internal' | 'external'

const Users = () => {
  const { t } = useTranslation()
  const user = useUser()
  const actorIsOwner = (user as any)?.rights === 'OWNER'
  const [audience, setAudience] = useState<Audience>('all')
  const [usersImportData, setUsersImportData] = useState<boolean>(false)
  const [userModal, setUserModal] = useState<boolean>(false)
  const { data, loading, refetch } = useQuery(Query.USERS)
  const [membershipTarget, setMembershipTarget] = useState<MembershipTarget | null>(null)
  const [deactivateUser, deactivateUserData] = useMutation(Mutation.DEACTIVATE_USER, {
    refetchQueries: [{ query: Query.USERS }],
    awaitRefetchQueries: true,
  })
  const [reactivateUser, reactivateUserData] = useMutation(Mutation.REACTIVATE_USER, {
    refetchQueries: [{ query: Query.USERS }],
    awaitRefetchQueries: true,
  })
  const [updateUserRights, updateUserRightsData] = useMutation(Mutation.UPDATE_USER_RIGHTS, {
    refetchQueries: [{ query: Query.USERS }],
  })

  const [editUserValues, setEditUserValues] = useState<null | {
    id?: string
    name: string
    email: string
    company?: string
    rights: string | null
    userToProjects?: { id: string; role: string; roleId: string | null; project: { id: string; name: string } }[]
  }>({
    name: '',
    email: '',
    company: '',
    rights: null,
  })

  const handleAdd = () => {
    setEditUserValues(null)
    setUserModal(true)
  }
  const handleMembershipConfirm = async () => {
    if (!membershipTarget) return
    const { user: target, mode } = membershipTarget
    const name = target.name || target.email

    try {
      if (mode === 'deactivate') {
        await deactivateUser({ variables: { id: target.id } })
      } else {
        await reactivateUser({ variables: { id: target.id } })
      }
      toastr.success(
        t('Common_success', 'Success'),
        mode === 'deactivate'
          ? t('Organization_users_deactivate_success', { name })
          : t('Organization_users_activate_success', { name }),
      )
      setMembershipTarget(null)
    } catch (error) {
      toastr.error(t('Common_error', 'Error'), (error as Error).message)
    }
  }

  const handleEdit = (user) => {
    setEditUserValues({
      id: user.id,
      name: user.name,
      email: user.email,
      company: user.company || '',
      rights: user.rights,
      userToProjects: user.userToProjects || [],
    })
    setUserModal(true)
  }

  const handleFileChange = ({ target, target: { files } }) => {
    Pappa.parse(files[0], {
      header: true,
      skipEmptyLines: true,
      complete: (d) => {
        setUsersImportData(d.data)
        target.files = null
      },
    })
  }

  // Template matching exactly what handleFileChange/UsersImportModal consume
  // (header row `name,email`). BOM keeps Excel happy with diacritics.
  const handleDownloadTemplate = () => {
    const csv = '\ufeffname,email\r\nJohn Smith,john.smith@example.com\r\nJane Doe,jane.doe@example.com\r\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'users-import-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleRightsChange = (value: string, row) => {
    updateUserRights({
      variables: {
        id: row.id,
        rights: value,
      },
      optimisticResponse: {
        updateUserRights: {
          __typename: 'User',
          ...row,
          rights: value,
        },
      },
    })
  }

  const allRows = data ? data.users : []
  const externalCount = allRows.filter((u: any) => u.rights === 'EXTERNAL').length
  const rows = useMemo(() => {
    const visible =
      audience === 'all'
        ? allRows
        : allRows.filter((u: any) =>
            audience === 'external' ? u.rights === 'EXTERNAL' : u.rights !== 'EXTERNAL')
    // Offboarded accounts stay in the roster (deactivation is reversible) but
    // sink below the people the org actually works with.
    return [...visible].sort(
      (a: any, b: any) => Number(!!a.deactivatedAt) - Number(!!b.deactivatedAt),
    )
  }, [allRows, audience])
  const isSavingMembership = deactivateUserData.loading || reactivateUserData.loading
  const isLoading = loading

  const audiences: { key: Audience; label: string; count: number }[] = [
    { key: 'all', label: t('Organization_users_audience_all'), count: allRows.length },
    { key: 'internal', label: t('Organization_users_audience_internal'), count: allRows.length - externalCount },
    { key: 'external', label: t('Organization_users_audience_external'), count: externalCount },
  ]

  return (
    <div>
      <Grid
        actionBar={{
          // @ts-ignore - React 18 type compatibility issue with custom Grid component
          actionButton: (
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-md border border-border p-0.5">
                {audiences.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setAudience(a.key)}
                    className={cn(
                      'h-7 rounded-sm px-2.5 text-sm transition-colors',
                      audience === a.key
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {a.label}
                    <span className="ml-1.5 tabular-nums opacity-70">{a.count}</span>
                  </button>
                ))}
              </div>
              <Button size="sm" onClick={() => handleAdd()}>
                <UserPlus className="h-4 w-4" />
                {t('Organization_users_new_user_btn')}
              </Button>
              <FileUpload
                name="users_csv"
                accept=".csv, text/csv"
                label={t('Organization_users_import_from_csv_btn')}
                onChange={handleFileChange}
              />
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <FileDown className="h-4 w-4" />
                {t('Organization_users_csv_template_btn')}
              </Button>
            </div>
          ),
        }}
        columns={[
          { title: t('Organization_users_name_label'), name: 'name', width: '40%' },
          { title: t('Organization_users_email_label'), name: 'email', width: '40%' },
          { title: t('Organization_users_rights_label'), name: 'rights' },
        ]}
        rows={rows}
        rowActions={[
          { title: t('Organization_users_edit_btn'), action: handleEdit },
          ...(actorIsOwner
            ? [
                {
                  title: t('Organization_users_deactivate_btn'),
                  color: 'danger',
                  // The backend rejects self-offboarding; do not offer it.
                  show: (row) => !row.deactivatedAt && row.id !== (user as any)?.id,
                  action: (row) => setMembershipTarget({ user: row, mode: 'deactivate' }),
                },
                {
                  title: t('Organization_users_activate_btn'),
                  show: (row) => !!row.deactivatedAt,
                  action: (row) => setMembershipTarget({ user: row, mode: 'activate' }),
                },
              ]
            : []),
        ]}
        formatters={[
          {
            formatter: ({ value, row }) => (
              <span
                className={cn(
                  'flex items-center gap-2',
                  row.deactivatedAt && 'text-muted-foreground',
                )}
              >
                <span className="truncate">{value}</span>
                {row.deactivatedAt && (
                  <Badge variant="neutral" className="whitespace-nowrap">
                    {t('Organization_users_deactivated_badge')}
                  </Badge>
                )}
              </span>
            ),
            for: ['name'],
          },
          {
            formatter: ({ value, row }) => (
              <Select
                disabled={row.id === (user as any)?.id || (!actorIsOwner && row.rights === 'OWNER')}
                defaultValue={value}
                onValueChange={(val) => handleRightsChange(val, row)}
              >
                <SelectTrigger
                  className="h-8 w-auto text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Enums.RIGHTS.filter((o) => actorIsOwner || o !== 'OWNER' || value === 'OWNER').map((o) => (
                    <SelectItem key={o} value={o}>
                      {t('rights_' + o)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ),
            for: ['rights'],
          },
        ]}
      />
      {isLoading && <Loading />}
      <UsersImportModal
        isOpen={!!usersImportData}
        users={usersImportData}
        onClose={() => setUsersImportData(false)}
      />
      <UserModal
        isOpen={!!userModal}
        onClose={() => setUserModal(false)}
        editUserValues={editUserValues}
        onSaved={refetch}
      />
      <MembershipDialog
        target={membershipTarget}
        saving={isSavingMembership}
        onCancel={() => setMembershipTarget(null)}
        onConfirm={handleMembershipConfirm}
      />
    </div>
  )
}

export default Users
